import type { AuditLogListParams, AuditLogListResponse } from '@signalvote/shared';
import { apiResponse } from './apiFetch';
import { campaignHeaders, defineQuery } from '../../lib/data';

const STALE = 5 * 60 * 1000;

/** A failed audit-log request names the status text, since the server's body is not read. */
async function getAuditLog<T>(path: string, what: string, campaignId: string | null): Promise<T> {
  const response = await apiResponse(path, { headers: campaignHeaders(campaignId) });
  if (!response.ok) throw new Error(`Failed to fetch ${what}: ${response.statusText}`);
  return (await response.json()) as T;
}

/**
 * One page of the audit log, filtered. The page screen and the per-entity history panel both read it;
 * `keepPrevious` holds the old page on screen while the next one loads.
 */
export const useFinanceAuditLogs = defineQuery({
  key: (params: AuditLogListParams) => ['finance', 'audit-logs', 'list', params],
  reads: 'GET /api/finance/audit-logs',
  staleTime: STALE,
  keepPrevious: true,
  fetch: (params, { campaignId }): Promise<AuditLogListResponse> => {
    const { page = 1, pageSize = 20, entityType, entityId, action, dateFrom, dateTo } = params;
    const searchParams = new URLSearchParams({ page: page.toString(), pageSize: pageSize.toString() });
    if (entityType) searchParams.append('entityType', entityType);
    if (entityId) searchParams.append('entityId', entityId);
    if (action) searchParams.append('action', action);
    if (dateFrom) searchParams.append('dateFrom', dateFrom);
    if (dateTo) searchParams.append('dateTo', dateTo);
    return getAuditLog(`/api/finance/audit-logs?${searchParams.toString()}`, 'audit logs', campaignId);
  },
});

export const useAuditLogEntityTypes = defineQuery({
  key: () => ['finance', 'audit-logs', 'entity-types'],
  reads: 'GET /api/finance/audit-logs/entity-types',
  staleTime: STALE,
  fetch: (_: undefined, { campaignId }): Promise<string[]> =>
    getAuditLog('/api/finance/audit-logs/entity-types', 'entity types', campaignId),
});

export const useAuditLogActions = defineQuery({
  key: () => ['finance', 'audit-logs', 'actions'],
  reads: 'GET /api/finance/audit-logs/actions',
  staleTime: STALE,
  fetch: (_: undefined, { campaignId }): Promise<string[]> =>
    getAuditLog('/api/finance/audit-logs/actions', 'actions', campaignId),
});
